import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { StressStrainChart } from './StressStrainChart';
import { fetchAllManufacturersData, type FilamentTestData } from '../../lib/filamentStressStrain';
import { getFilamentType } from '../../data/filamentTestData';

interface FilamentStressStrainChartProps {
  filamentId: string;
  filamentName: string;
}

type Pattern = 'pattern1' | 'pattern2' | 'pattern3';

const TEXT_JA = {
  title: '応力–ひずみ曲線（Stress–Strain）',
  description: '3点曲げ試験による応力–ひずみ曲線です。横軸はひずみ（%）、縦軸は応力（MPa）を示します。メーカーごとの比較を表示しています。',
  loading: 'データ読み込み中...',
  noDataTitle: 'このフィラメントの曲げ試験データはまだ登録されていません。',
  noDataDesc: 'データが追加され次第、グラフが表示されます。',
  patternLabel: '測定方向：',
  pattern1: 'パターン1',
  pattern2: 'パターン2',
  pattern3: 'パターン3',
  noDataSuffix: ' (データなし)',
  downloadLink: 'この材料のCSVをダウンロードする',
  dataUsageTitle: 'データ利用について',
  dataUsageDesc: '個人利用は自由ですが、',
  dataUsageDescBold: '動画・記事・SNS等での公開、データの転載・再配布を行う場合は必ず事前にお問い合わせください。',
  dataUsageDescEnd: '詳細は',
  dataPolicy: 'データ利用規約',
  dataUsageDescEnd2: 'をご確認ください。',
  testMethodLink: '試験方法の詳細を見る'
};

const TEXT_EN = {
  title: 'Stress–Strain Curve',
  description: 'Stress–strain curve from three-point bending test. Horizontal axis shows strain (%), vertical axis shows stress (MPa). Comparison across manufacturers is displayed.',
  loading: 'Loading data...',
  noDataTitle: 'Bending test data for this filament has not been registered yet.',
  noDataDesc: 'The graph will be displayed once data is added.',
  patternLabel: 'Measurement direction:',
  pattern1: 'Pattern 1',
  pattern2: 'Pattern 2',
  pattern3: 'Pattern 3',
  noDataSuffix: ' (No data)',
  downloadLink: 'Download CSV for this material',
  dataUsageTitle: 'About Data Usage',
  dataUsageDesc: 'Personal use is free, but ',
  dataUsageDescBold: 'please contact us in advance if you plan to publish in videos, articles, SNS, or redistribute the data.',
  dataUsageDescEnd: 'For details, see ',
  dataPolicy: 'Data Usage Policy',
  dataUsageDescEnd2: '.',
  testMethodLink: 'View test method details'
};

/**
 * 個別フィラメントページ用の応力-ひずみグラフコンポーネント
 * メーカー比較表示（同一typeの全メーカーデータを表示）
 */
export const FilamentStressStrainChart: React.FC<FilamentStressStrainChartProps> = ({
  filamentId,
  filamentName
}) => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;

  const [selectedPattern, setSelectedPattern] = useState<Pattern>('pattern1');
  const [dataByPattern, setDataByPattern] = useState<Map<Pattern, FilamentTestData[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const filamentType = getFilamentType(filamentId);

        console.log(`[${filamentId}] メーカー比較データ読み込み開始:`, { filamentType });

        // 3パターン全て読み込み
        const patterns: Pattern[] = ['pattern1', 'pattern2', 'pattern3'];
        const newDataByPattern = new Map<Pattern, FilamentTestData[]>();

        for (const pattern of patterns) {
          const data = await fetchAllManufacturersData(filamentType, pattern);
          newDataByPattern.set(pattern, data);
          console.log(`[${filamentId}] ${pattern}: ${data.length} メーカー`);
        }

        setDataByPattern(newDataByPattern);

        // いずれかのパターンにデータがあるか確認
        const hasAnyData = Array.from(newDataByPattern.values()).some(arr => arr.length > 0);
        if (!hasAnyData) {
          setError('データが見つかりません');
        }
      } catch (err) {
        console.error(`[${filamentId}] データ読み込みエラー:`, err);
        setError('データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [filamentId]);

  const currentData = dataByPattern.get(selectedPattern) || [];

  // ローディング中
  if (loading) {
    return (
      <div className="metal-panel p-6" data-graph-lang={isEnglish ? 'en' : 'ja'}>
        <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
          {TEXT.title}
        </h2>
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <i className="ri-loader-4-line text-accent text-2xl animate-spin"></i>
            <span className="text-metal-light text-sm">{TEXT.loading}</span>
          </div>
        </div>
      </div>
    );
  }

  // データが無い場合
  if (error || currentData.length === 0) {
    return (
      <div className="metal-panel p-6" data-graph-lang={isEnglish ? 'en' : 'ja'}>
        <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
          {TEXT.title}
        </h2>
        <div className="flex items-start space-x-3 bg-metal p-6 rounded">
          <i className="ri-information-line text-metal-light text-lg mt-0.5"></i>
          <div className="text-metal-light text-sm">
            <p className="mb-2">{TEXT.noDataTitle}</p>
            <p className="text-xs">{TEXT.noDataDesc}</p>
          </div>
        </div>
      </div>
    );
  }

  // パターン表示名
  const patternLabels: Record<Pattern, string> = {
    pattern1: TEXT.pattern1,
    pattern2: TEXT.pattern2,
    pattern3: TEXT.pattern3
  };

  // データがある場合はグラフを表示
  return (
    <div className="metal-panel p-6" data-graph-lang={isEnglish ? 'en' : 'ja'}>
      <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
        {TEXT.title}
      </h2>
      <p className="text-metal-light text-sm mb-4">
        {TEXT.description}
      </p>

      {/* パターン切替ボタン */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-metal-light text-sm">{TEXT.patternLabel}</span>
        <div className="flex gap-2">
          {(['pattern1', 'pattern2', 'pattern3'] as Pattern[]).map(pattern => {
            const patternData = dataByPattern.get(pattern) || [];
            const hasData = patternData.length > 0;
            const isSelected = selectedPattern === pattern;
            
            return (
              <button
                key={pattern}
                onClick={() => setSelectedPattern(pattern)}
                disabled={!hasData}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors cursor-pointer whitespace-nowrap ${
                  isSelected
                    ? 'bg-accent text-white'
                    : hasData
                    ? 'bg-transparent border border-metal-light/40 text-metal-light hover:border-metal-light hover:bg-metal-light/10 hover:text-white'
                    : 'bg-transparent border border-metal-light/20 text-metal-light/40 cursor-not-allowed'
                }`}
              >
                {patternLabels[pattern]}
                {!hasData && TEXT.noDataSuffix}
              </button>
            );
          })}
        </div>
      </div>

      <StressStrainChart datasets={currentData} />

      {/* CSVダウンロードページへの導線 */}
      <div className="mt-6 text-center">
        <Link 
          to={isEnglish ? '/en/filament/data-download' : '/filament/data-download'}
          className="inline-flex items-center gap-2 text-accent hover:text-accent-orange text-sm transition-colors"
        >
          <i className="ri-download-2-line"></i>
          {TEXT.downloadLink}
        </Link>
      </div>

      {/* データ利用に関する注意書き */}
      <div className="mt-6 flex items-start space-x-3 bg-accent/10 border border-accent/30 p-4 rounded">
        <i className="ri-alert-line text-accent text-lg mt-0.5 flex-shrink-0"></i>
        <div className="text-sm">
          <p className="text-white font-semibold mb-1">{TEXT.dataUsageTitle}</p>
          <p className="text-metal-light">
            {TEXT.dataUsageDesc}<strong className="text-white">{TEXT.dataUsageDescBold}</strong>
            {TEXT.dataUsageDescEnd}<Link to={isEnglish ? '/en/data-policy' : '/data-policy'} className="text-accent hover:text-accent-orange underline transition-colors">{TEXT.dataPolicy}</Link>{TEXT.dataUsageDescEnd2}
          </p>
        </div>
      </div>

      {/* 試験方法へのリンク */}
      <div className="mt-4 text-center">
        <Link 
          to={isEnglish ? '/en/filament/bend-strength-test' : '/filament/bend-strength-test'}
          className="inline-flex items-center gap-2 text-accent hover:text-accent-orange text-sm transition-colors"
        >
          <i className="ri-flask-line"></i>
          {TEXT.testMethodLink}
        </Link>
      </div>
    </div>
  );
};
